// src/components/ReactionBar.tsx

import React, { useEffect, useState, useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import { getReactions, Reaction, toggleReaction } from '../api/reactions';

type Props = {
    commentId: string;
    currentUserEmail: string;
    refreshTrigger?: number;
};

const ReactionBar: React.FC<Props> = ({
    commentId,
    refreshTrigger,
    currentUserEmail,
}) => {
    const [reactions, setReactions] = useState<Reaction[]>([]);

    useEffect(() => {
        const load = async () => {
            try {
                const data = await getReactions(commentId);
                setReactions(data);
            } catch (err) {
                console.warn(`Failed to load reactions for ${commentId}`, err);
                setReactions([]);
            }
        };
        load();
    }, [commentId, refreshTrigger]);

    const sortedGroups = useMemo(() => {
        const map: { [emoji: string]: Reaction[] } = {};
        for (const r of reactions) {
            if (!map[r.emoji]) map[r.emoji] = [];
            map[r.emoji].push(r);
        }

        return Object.entries(map).sort(([, listA], [, listB]) => {
            const countDiff = listB.length - listA.length;
            if (countDiff !== 0) return countDiff;
            const dateA = new Date(listA[0].created_at).getTime();
            const dateB = new Date(listB[0].created_at).getTime();
            return dateA - dateB;
        });
    }, [reactions]);

    if (sortedGroups.length === 0) return null;

    const handleToggle = async (emoji: string) => {
        try {
            await toggleReaction(commentId, emoji);
            const updated = await getReactions(commentId);
            setReactions(updated);
        } catch (err) {
            console.error('Failed to toggle reaction', err);
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            {sortedGroups.map(([emoji, list]) => {
                const count = list.length;
                const userHasReacted = list.some(
                    (r) => r.user_email === currentUserEmail
                );

                return (
                    <Tooltip
                        key={emoji}
                        title={list.map((r) => r.user_email).join(', ')}
                    >
                        <Box
                            onClick={() => handleToggle(emoji)}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                borderRadius: '999px',
                                border: '1px solid',
                                borderColor: userHasReacted
                                    ? '#8888ff'
                                    : '#ccc',
                                px: 1,
                                py: 0.25,
                                cursor: 'pointer',
                                fontSize: 14,
                                bgcolor: userHasReacted ? '#dfe3ff' : '#f9f9f9',
                                transition:
                                    'background-color 0.15s ease-in-out, border-color 0.15s',
                                '&:hover': {
                                    bgcolor: userHasReacted
                                        ? '#c9ceff'
                                        : '#e0e0ff',
                                },
                            }}
                        >
                            <span style={{ marginRight: 4 }}>{emoji}</span>
                            <span>{count}</span>
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
};

export default ReactionBar;
